import { cn } from "@tcg/ui";
import { CheckCircle2, Plug, Printer, TriangleAlert } from "lucide-react";
import { useState } from "react";
import { useTranslation } from "react-i18next";

import { ApprovalCancelled, useApproval } from "@/app/approval";
import { useCan, usePos } from "@/app/context";
import { useToast } from "@/components/Toast";
import { call } from "@/lib/api";
import { errorText } from "@/lib/errors";
import {
  isPrinterError,
  type PrinterConnection,
  type PrinterSettings as Settings,
} from "@/lib/printer";
import { decodeEscPos } from "@/lib/printer/decode";
import { base64ToBytes } from "@/lib/printer/escpos";

const CONNECTIONS: PrinterConnection[] = ["webusb", "bluetooth", "network", "mock", "none"];
const GUIDE: Record<PrinterConnection, string[]> = {
  webusb: ["guide_usb_1", "guide_usb_2", "guide_usb_3", "guide_usb_4"],
  bluetooth: ["guide_bt_1", "guide_bt_2", "guide_bt_3"],
  network: ["guide_net_1", "guide_net_2", "guide_net_3"],
  mock: ["guide_mock_1"],
  none: ["guide_none_1"],
};

/**
 * レシートプリンター (T07-34, design/05 §6): the way the printer is connected (USB cable /
 * Bluetooth / print bridge on the LAN for iPad), a step-by-step guide on screen, 「プリンターを選ぶ」
 * (the browser's chooser needs this tap the first time), paper width, the drawer, 「テスト印刷」 (a page
 * pos-service made: five languages + QR + cut) and 「ドロワーを開く」 (`pos.open_drawer`, else the
 * manager's PIN).
 */
export function PrinterSettings() {
  const { t } = useTranslation();
  const { printer, api, auth } = usePos();
  const { ask } = useApproval();
  const canDrawer = useCan("pos.open_drawer");
  const toast = useToast();
  const [draft, setDraft] = useState<Settings>(printer.config);
  const [status, setStatus] = useState<{ ok: boolean; text: string } | null>(null);
  const [busy, setBusy] = useState(false);
  const set = (changes: Partial<Settings>) => setDraft((d) => ({ ...d, ...changes }));

  const save = async () => {
    await printer.save({ ...draft, usbDevice: printer.config.usbDevice ?? null });
    toast.show(t("settings.saved"), "success");
  };

  const choose = async () => {
    await printer.save({ ...draft, usbDevice: null });
    try {
      const result = await printer.driver().connect();
      setStatus(
        result.connected
          ? {
              ok: true,
              text: `${t("settings.status_ok")}${result.name ? ` — ${result.name}` : ""}`,
            }
          : { ok: false, text: t(`print.${result.error ?? "not_found"}`) },
      );
    } catch (error) {
      setStatus({
        ok: false,
        text: t(`print.${isPrinterError(error) ? error.code : "not_found"}`),
      });
    }
  };

  const bridgeTest = async () => {
    await printer.save(draft);
    try {
      const result = await printer.driver().status();
      setStatus(
        result.connected
          ? {
              ok: true,
              text: `${t("settings.status_ok")}${result.name ? ` — ${result.name}` : ""}`,
            }
          : { ok: false, text: t(`print.${result.error ?? "bridge_unreachable"}`) },
      );
    } catch (error) {
      setStatus({
        ok: false,
        text: t(`print.${isPrinterError(error) ? error.code : "bridge_unreachable"}`),
      });
    }
  };

  const testPrint = async () => {
    setBusy(true);
    setStatus(null);
    try {
      await printer.save({ ...draft, usbDevice: printer.config.usbDevice ?? null });
      const page = await call(() =>
        api.GET("/v1/pos/registers/current/test-print", {
          params: { query: { width: draft.paperWidth } },
        }),
      );
      const check = decodeEscPos(base64ToBytes(page.data));
      const job = await printer.queue.submit({ label: t("settings.test_print"), data: page.data });
      setStatus(
        job.status === "done"
          ? {
              ok: true,
              text: t("settings.test_printed", {
                mode: t(`settings.mode_${page.mode}`),
                qr: check.qr || page.mode === "raster" ? "✓" : "—",
              }),
            }
          : { ok: false, text: job.lastError ?? t("print.write_failed") },
      );
    } catch (error) {
      setStatus({ ok: false, text: errorText(error, t) });
    } finally {
      setBusy(false);
    }
  };

  const drawer = async () => {
    if (!canDrawer) {
      try {
        const state = auth.getSnapshot();
        await ask({
          permission: "pos.open_drawer",
          action: "drawer.open",
          subject: state.kind === "unlocked" ? state.device.registerId : "register",
          reason: t("settings.drawer_reason"),
        });
      } catch (error) {
        if (error instanceof ApprovalCancelled) {
          return;
        }
        throw error;
      }
    }
    const result = await printer.queue.openDrawer();
    setStatus(
      result.ok
        ? { ok: true, text: t("done.drawer_opened") }
        : { ok: false, text: t(`print.${result.error ?? "write_failed"}`) },
    );
  };

  return (
    <section aria-labelledby="printer-title" className="flex flex-col gap-4">
      <h2 id="printer-title" className="text-2xl font-semibold">
        {t("settings.printer")}
      </h2>
      <fieldset className="flex flex-wrap gap-2">
        <legend className="mb-1 font-medium">{t("settings.connection")}</legend>
        {CONNECTIONS.map((c) => (
          <button
            key={c}
            type="button"
            aria-pressed={draft.connection === c}
            onClick={() => set({ connection: c })}
            className={cn(
              "min-h-pos-touch rounded-md border px-5 text-lg",
              draft.connection === c && "border-primary bg-primary text-primary-foreground",
            )}
          >
            {t(`settings.connection_${c}`)}
          </button>
        ))}
      </fieldset>
      <ol
        aria-label={t("settings.guide")}
        className="list-decimal rounded-md bg-muted p-4 pl-8 text-base"
      >
        {GUIDE[draft.connection].map((key) => (
          <li key={key} className="py-0.5">
            {t(`settings.${key}`)}
          </li>
        ))}
      </ol>
      <fieldset className="flex gap-2">
        <legend className="mb-1 font-medium">{t("settings.paper")}</legend>
        {([58, 80] as const).map((w) => (
          <button
            key={w}
            type="button"
            aria-pressed={draft.paperWidth === w}
            onClick={() => set({ paperWidth: w })}
            className={cn(
              "min-h-pos-touch rounded-md border px-6 text-lg",
              draft.paperWidth === w && "border-primary bg-primary text-primary-foreground",
            )}
          >
            {w} mm
          </button>
        ))}
      </fieldset>
      {draft.connection === "network" ? (
        <div className="grid gap-3 md:grid-cols-3">
          <label className="flex flex-col gap-1">
            <span className="font-medium">{t("settings.bridge_url")}</span>
            <input
              value={draft.bridgeUrl ?? ""}
              onChange={(e) => set({ bridgeUrl: e.target.value || null })}
              placeholder="https://print-bridge.local:9443"
              className="min-h-pos-touch rounded-md border px-3"
            />
          </label>
          <label className="flex flex-col gap-1">
            <span className="font-medium">{t("settings.bridge_token")}</span>
            <input
              type="password"
              value={draft.bridgeToken ?? ""}
              onChange={(e) => set({ bridgeToken: e.target.value || null })}
              autoComplete="off"
              className="min-h-pos-touch rounded-md border px-3"
            />
          </label>
          <label className="flex flex-col gap-1">
            <span className="font-medium">{t("settings.bridge_printer")}</span>
            <input
              value={draft.bridgePrinter ?? ""}
              onChange={(e) => set({ bridgePrinter: e.target.value || null })}
              placeholder="front"
              className="min-h-pos-touch rounded-md border px-3"
            />
          </label>
        </div>
      ) : null}
      <label className="flex min-h-12 items-center gap-3">
        <input
          type="checkbox"
          checked={draft.drawer}
          onChange={(e) => set({ drawer: e.target.checked })}
          className="size-6"
        />
        {t("settings.drawer")}
      </label>
      <div className="flex flex-wrap gap-2">
        {draft.connection === "webusb" || draft.connection === "bluetooth" ? (
          <button
            type="button"
            onClick={() => void choose()}
            className="flex min-h-pos-touch items-center gap-2 rounded-md border px-5 text-lg"
          >
            <Plug aria-hidden className="size-5" />
            {t("settings.choose_printer")}
          </button>
        ) : null}
        {draft.connection === "network" ? (
          <button
            type="button"
            onClick={() => void bridgeTest()}
            className="flex min-h-pos-touch items-center gap-2 rounded-md border px-5 text-lg"
          >
            <Plug aria-hidden className="size-5" />
            {t("settings.bridge_test")}
          </button>
        ) : null}
        <button
          type="button"
          onClick={() => void testPrint()}
          disabled={busy || draft.connection === "none"}
          className="flex min-h-pos-touch items-center gap-2 rounded-md border px-5 text-lg disabled:opacity-50"
        >
          <Printer aria-hidden className="size-5" />
          {t("settings.test_print")}
        </button>
        <button
          type="button"
          onClick={() => void drawer()}
          className="min-h-pos-touch rounded-md border px-5 text-lg"
        >
          {t("settings.test_drawer")}
        </button>
        <button
          type="button"
          onClick={() => void save()}
          className="min-h-pos-touch rounded-md bg-primary px-8 text-lg font-semibold text-primary-foreground"
        >
          {t("settings.save")}
        </button>
      </div>
      {status ? (
        <p
          role="status"
          className={cn(
            "flex items-center gap-2 text-lg",
            status.ok ? "text-pos-ok" : "text-pos-problem",
          )}
        >
          {status.ok ? (
            <CheckCircle2 aria-hidden className="size-6" />
          ) : (
            <TriangleAlert aria-hidden className="size-6" />
          )}
          {status.text}
        </p>
      ) : null}
    </section>
  );
}
